import { FC, HTMLAttributes, useEffect, useMemo, useState } from "react";
import { useStyles } from "./RankGemTag.style";
import clsx from "classnames";
import {
  Typography,
  TypographyVariant,
  TypographyColor,
  Icon,
  Image,
} from "@onmo/ui";
import Cup from "../../../assets/icons/cup_1x.webp";
import Cup2x from "../../../assets/icons/cup_2x.webp";
import Cup3x from "../../../assets/icons/cup_3x.webp";
import Gem from "../../../assets/icons/gem_1x.webp";
import Gem2x from "../../../assets/icons/gem_2x.webp";
import Gem3x from "../../../assets/icons/gem_3x.webp";
import { useRankInfo } from "../../../hooks/useRankInfo";
import { useNavigate } from "react-router-dom";
import { PageRoutes } from "../../../constants/routes";
import { useQuery } from "@apollo/client";
import { meGQL, Query } from "@onmo/graphql/graphql";
import isNil from "lodash-es/isNil";
import { useTranslation } from "react-i18next";

interface IRankGemTag extends HTMLAttributes<HTMLDivElement> {
  isResult?: boolean;
  isWin?: boolean;
}

export const RankGemTag: FC<IRankGemTag> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isResult = false, isWin = false, ...otherProps } = props;

  const [showRank, setShowRank] = useState(!isResult);
  const [fadeOut, setFadeOut] = useState(false);
  const { data: dataMe } = useQuery<Query>(meGQL, {
    fetchPolicy: "cache-first",
  });

  const { newRankWithState } = useRankInfo({
    rank: dataMe?.me?.leaderboardRank,
    type: "allTime",
  });

  const rankContent = useMemo(() => {
    if (isNil(newRankWithState?.rank)) return "0";
    if (newRankWithState.rank >= 999) return `999+`;
    return newRankWithState.rank + 1;
  }, [newRankWithState?.rank]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeOut(true);
      setTimeout(() => {
        setShowRank((prev) => !prev);
        setFadeOut(false);
      }, 300);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const renderResultContent = () => {
    if (isResult) {
      return isWin ? (
        <>
          <Typography
            variant={TypographyVariant.BodyB4Bold}
            color={TypographyColor.Secondary}
            className={classes.rank}
          >
            {`#${rankContent}`}
          </Typography>
          {newRankWithState?.rankState && (
            <Icon
              name={
                newRankWithState.rankState === "up"
                  ? "icon-rank-up"
                  : "icon-rank-down"
              }
              size={16}
              className={
                newRankWithState.rankState === "up"
                  ? classes.directionUp
                  : classes.directionDown
              }
            />
          )}
        </>
      ) : (
        <Typography
          variant={TypographyVariant.BodyB5Bold}
          color={TypographyColor.Secondary}
          className={classes.rank}
        >
          {t("leaderboard")}
        </Typography>
      );
    }

    return (
      <>
        <Typography
          variant={TypographyVariant.BodyB4Bold}
          color={TypographyColor.Secondary}
          className={classes.rank}
        >
          {`#${rankContent}`}
        </Typography>
        {newRankWithState?.rankState && (
          <Icon
            name={
              newRankWithState.rankState === "up"
                ? "icon-rank-up"
                : "icon-rank-down"
            }
            size={16}
            className={
              newRankWithState.rankState === "up"
                ? classes.directionUp
                : classes.directionDown
            }
          />
        )}
      </>
    );
  };

  return (
    <div
      className={classes.container}
      onClick={() => navigate(PageRoutes.Leaderboard)}
      {...otherProps}
    >
      <div className={clsx(classes.fade, fadeOut && classes.hidden)}>
        {showRank ? (
          <div className={classes.content}>
            <Image src={Cup} src2x={Cup2x} src3x={Cup3x} alt="cup" width={16} />
            {renderResultContent()}
          </div>
        ) : (
          <div className={classes.content}>
            <Image src={Gem} src2x={Gem2x} src3x={Gem3x} alt="gem" width={16} />
            <Typography
              variant={TypographyVariant.BodyB4Bold}
              color={TypographyColor.Secondary}
              className={classes.rank}
            >
              {dataMe?.me.xp ?? 0}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};
